import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Copy, KeyRound, Loader2, Pencil, Plus, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarDataHora, paraBooleano } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import type { UbsResumida, Usuario } from '@/features/admin/tipos';
import { PERFIS, ROTULOS_PERFIL, type PerfilUsuario } from '@/features/auth/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { BarraBusca } from '@/components/barra-busca';
import { Paginacao } from '@/components/paginacao';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const esquema = z
  .object({
    nome: z.string().min(3, 'Informe o nome completo.'),
    email: z.string().min(1, 'Informe o e-mail.').email('Informe um e-mail válido.'),
    perfil: z.enum(PERFIS, { message: 'Selecione o perfil.' }),
    ubsId: z.string().optional(),
    ubsIds: z.array(z.string()).optional(),
    ativo: z.boolean().optional(),
  })
  .refine((dados) => dados.perfil !== 'UBS' || Boolean(dados.ubsId), {
    message: 'Usuários do perfil UBS precisam estar vinculados a uma unidade.',
    path: ['ubsId'],
  })
  .refine((dados) => dados.perfil !== 'GESTOR' || (dados.ubsIds?.length ?? 0) > 0, {
    message: 'O gestor precisa acompanhar ao menos uma unidade.',
    path: ['ubsIds'],
  });

type Formulario = z.infer<typeof esquema>;

const COLUNAS = 6;

export function PaginaUsuarios() {
  const queryClient = useQueryClient();
  const [pagina, setPagina] = React.useState(1);
  const [busca, setBusca] = React.useState('');
  const [filtroPerfil, setFiltroPerfil] = React.useState<'' | PerfilUsuario>('');
  const [emEdicao, setEmEdicao] = React.useState<Usuario | null>(null);
  const [dialogoAberto, setDialogoAberto] = React.useState(false);
  const [senhaGerada, setSenhaGerada] = React.useState<{ nome: string; senha: string } | null>(
    null,
  );

  const parametros = new URLSearchParams({ pagina: String(pagina), limite: '20' });
  if (busca) parametros.set('busca', busca);
  if (filtroPerfil) parametros.set('perfil', filtroPerfil);

  const { data, isPending } = useQuery({
    queryKey: ['usuarios', pagina, busca, filtroPerfil],
    queryFn: () => api.get<RespostaPaginada<Usuario>>(`/usuarios?${parametros.toString()}`),
  });

  const { data: unidades } = useQuery({
    queryKey: ['ubs-ativas'],
    queryFn: () => api.get<UbsResumida[]>('/ubs/ativas'),
    staleTime: 5 * 60_000,
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: { nome: '', email: '', perfil: 'UBS', ubsId: '', ubsIds: [] },
  });

  const perfilSelecionado = formulario.watch('perfil');

  const abrirCriacao = () => {
    setEmEdicao(null);
    formulario.reset({ nome: '', email: '', perfil: 'UBS', ubsId: '', ubsIds: [] });
    setDialogoAberto(true);
  };

  const abrirEdicao = (usuario: Usuario) => {
    setEmEdicao(usuario);
    formulario.reset({
      nome: usuario.nome,
      email: usuario.email,
      perfil: usuario.perfil,
      ubsId: usuario.ubsId ?? '',
      ubsIds: usuario.ubsGeridas?.map((vinculo) => vinculo.ubs.id) ?? [],
      ativo: usuario.ativo,
    });
    setDialogoAberto(true);
  };

  const salvar = useMutation({
    mutationFn: async (dados: Formulario) => {
      const ubsId = dados.perfil === 'UBS' ? dados.ubsId : undefined;
      const ubsIds = dados.perfil === 'GESTOR' ? dados.ubsIds : undefined;

      if (emEdicao) {
        return api.patch<Usuario>(`/usuarios/${emEdicao.id}`, {
          nome: dados.nome,
          perfil: dados.perfil,
          ativo: dados.ativo,
          ubsId,
          ubsIds,
        });
      }

      const criado = await api.post<{ usuario: Usuario; senhaProvisoria: string }>('/usuarios', {
        nome: dados.nome,
        email: dados.email,
        perfil: dados.perfil,
        ubsId,
        ubsIds,
      });

      setSenhaGerada({ nome: criado.usuario.nome, senha: criado.senhaProvisoria });
      return criado.usuario;
    },
    onSuccess: async () => {
      if (emEdicao) toast.success('Usuário atualizado.');
      setDialogoAberto(false);
      await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.');
    },
  });

  const resetarSenha = useMutation({
    mutationFn: (usuario: Usuario) =>
      api
        .post<{ senhaProvisoria: string }>(`/usuarios/${usuario.id}/resetar-senha`)
        .then((r) => ({ nome: usuario.nome, senha: r.senhaProvisoria })),
    onSuccess: async (resultado) => {
      setSenhaGerada(resultado);
      await queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    },
    onError: (erro) => {
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível gerar a senha.',
      );
    },
  });

  const aoEnviar = formulario.handleSubmit((dados) => salvar.mutate(dados));

  const copiarSenha = async () => {
    if (!senhaGerada) return;
    try {
      await navigator.clipboard.writeText(senhaGerada.senha);
      toast.success('Senha copiada.');
    } catch {
      toast.error('Não foi possível copiar. Anote a senha manualmente.');
    }
  };

  return (
    <>
      <CabecalhoPagina
        titulo="Usuários"
        descricao="Contas de acesso ao sistema, com o perfil que define o que cada uma enxerga."
        acoes={
          <Botao onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Novo usuário
          </Botao>
        }
      />

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <BarraBusca
          valor={busca}
          aoMudar={(valor) => {
            setBusca(valor);
            setPagina(1);
          }}
          placeholder="Buscar por nome ou e-mail"
        />
        <Selecao
          value={filtroPerfil}
          onChange={(evento) => {
            setFiltroPerfil(evento.target.value as '' | PerfilUsuario);
            setPagina(1);
          }}
          aria-label="Filtrar por perfil"
          className="w-full sm:w-48"
        >
          <option value="">Todos os perfis</option>
          {PERFIS.map((perfil) => (
            <option key={perfil} value={perfil}>
              {ROTULOS_PERFIL[perfil]}
            </option>
          ))}
        </Selecao>
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Nome</TabelaTitulo>
            <TabelaTitulo>Perfil</TabelaTitulo>
            <TabelaTitulo>UBS</TabelaTitulo>
            <TabelaTitulo>Último acesso</TabelaTitulo>
            <TabelaTitulo>Situação</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>Nenhum usuário encontrado.</TabelaVazia>
          )}

          {data?.dados.map((usuario) => (
            <TabelaLinha key={usuario.id}>
              <TabelaCelula rotulo="Nome">
                <div className="font-medium">{usuario.nome}</div>
                <div className="text-silenciado-texto text-xs">{usuario.email}</div>
              </TabelaCelula>
              <TabelaCelula rotulo="Perfil">
                <Etiqueta tom={usuario.perfil === 'ADMINISTRADOR' ? 'marca' : 'neutro'}>
                  {ROTULOS_PERFIL[usuario.perfil]}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula rotulo="UBS" className="text-silenciado-texto">
                {usuario.perfil === 'GESTOR'
                  ? (usuario.ubsGeridas?.map((vinculo) => vinculo.ubs.nome).join(', ') ?? '—')
                  : (usuario.ubs?.nome ?? '—')}
              </TabelaCelula>
              <TabelaCelula rotulo="Último acesso" className="text-silenciado-texto text-xs">
                {formatarDataHora(usuario.ultimoLoginEm)}
              </TabelaCelula>
              <TabelaCelula rotulo="Situação">
                <div className="flex flex-wrap gap-1">
                  <Etiqueta tom={usuario.ativo ? 'sucesso' : 'neutro'}>
                    {usuario.ativo ? 'Ativo' : 'Inativo'}
                  </Etiqueta>
                  {usuario.precisaTrocarSenha && (
                    <Etiqueta tom="alerta">
                      <ShieldAlert className="size-3" aria-hidden="true" />
                      Senha provisória
                    </Etiqueta>
                  )}
                </div>
              </TabelaCelula>
              <TabelaCelula className="text-right whitespace-nowrap">
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => resetarSenha.mutate(usuario)}
                  disabled={resetarSenha.isPending}
                  aria-label={`Gerar nova senha para ${usuario.nome}`}
                  title="Gera uma senha provisória e encerra as sessões do usuário"
                >
                  <KeyRound aria-hidden="true" />
                </Botao>
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => abrirEdicao(usuario)}
                  aria-label={`Editar ${usuario.nome}`}
                >
                  <Pencil aria-hidden="true" />
                  Editar
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={setPagina} />}

      {/* Formulário de criação e edição */}
      <Dialogo open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>{emEdicao ? 'Editar usuário' : 'Novo usuário'}</DialogoTitulo>
            <DialogoDescricao>
              {emEdicao
                ? 'Trocar o perfil ou inativar a conta encerra as sessões abertas na hora.'
                : 'O sistema gera uma senha provisória, exibida uma única vez após o cadastro.'}
            </DialogoDescricao>
          </DialogoCabecalho>

          <form onSubmit={(evento) => void aoEnviar(evento)} noValidate>
            <DialogoCorpo>
              <Campo
                id="usuario-nome"
                rotulo="Nome completo"
                obrigatorio
                erro={formulario.formState.errors.nome?.message}
              >
                <Entrada autoFocus {...formulario.register('nome')} />
              </Campo>

              <Campo
                id="usuario-email"
                rotulo="E-mail"
                obrigatorio
                ajuda={emEdicao ? 'O e-mail não pode ser alterado após o cadastro.' : undefined}
                erro={formulario.formState.errors.email?.message}
              >
                <Entrada
                  type="email"
                  disabled={Boolean(emEdicao)}
                  {...formulario.register('email')}
                />
              </Campo>

              <Campo
                id="usuario-perfil"
                rotulo="Perfil"
                obrigatorio
                erro={formulario.formState.errors.perfil?.message}
              >
                <Selecao {...formulario.register('perfil')}>
                  {PERFIS.map((perfil) => (
                    <option key={perfil} value={perfil}>
                      {ROTULOS_PERFIL[perfil]}
                    </option>
                  ))}
                </Selecao>
              </Campo>

              {perfilSelecionado === 'GESTOR' && (
                <Campo
                  id="usuario-ubs-geridas"
                  rotulo="Unidades acompanhadas"
                  obrigatorio
                  ajuda="O gestor enxerga o fluxo e os relatórios apenas destas unidades."
                  erro={formulario.formState.errors.ubsIds?.message}
                >
                  <Controller
                    control={formulario.control}
                    name="ubsIds"
                    render={({ field }) => (
                      <ul className="border-borda max-h-48 overflow-y-auto rounded-md border">
                        {unidades?.length === 0 && (
                          <li className="text-silenciado-texto p-3 text-sm">
                            Nenhuma UBS cadastrada.
                          </li>
                        )}
                        {unidades?.map((unidade) => {
                          const marcada = field.value?.includes(unidade.id) ?? false;
                          return (
                            <li key={unidade.id} className="border-borda border-b last:border-b-0">
                              <label className="hover:bg-silenciado flex cursor-pointer items-center gap-2 p-2 text-sm">
                                <input
                                  type="checkbox"
                                  className="accent-marca size-4"
                                  checked={marcada}
                                  onChange={() =>
                                    field.onChange(
                                      marcada
                                        ? (field.value ?? []).filter((id) => id !== unidade.id)
                                        : [...(field.value ?? []), unidade.id],
                                    )
                                  }
                                />
                                {unidade.nome}
                              </label>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  />
                </Campo>
              )}

              {perfilSelecionado === 'UBS' && (
                <Campo
                  id="usuario-ubs"
                  rotulo="UBS de vínculo"
                  obrigatorio
                  ajuda="Define quais solicitações o usuário poderá ver."
                  erro={formulario.formState.errors.ubsId?.message}
                >
                  <Selecao {...formulario.register('ubsId')}>
                    <option value="">Selecione a unidade…</option>
                    {unidades?.map((unidade) => (
                      <option key={unidade.id} value={unidade.id}>
                        {unidade.nome}
                      </option>
                    ))}
                  </Selecao>
                </Campo>
              )}

              {emEdicao && (
                <Campo id="usuario-ativo" rotulo="Situação">
                  <Selecao
                    {...formulario.register('ativo', {
                      setValueAs: paraBooleano,
                    })}
                  >
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </Selecao>
                </Campo>
              )}
            </DialogoCorpo>

            <DialogoRodape>
              <Botao variante="contorno" onClick={() => setDialogoAberto(false)}>
                Cancelar
              </Botao>
              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                Salvar
              </Botao>
            </DialogoRodape>
          </form>
        </DialogoConteudo>
      </Dialogo>

      {/* Senha provisória — exibida uma única vez */}
      <Dialogo open={senhaGerada !== null} onOpenChange={() => setSenhaGerada(null)}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Senha provisória gerada</DialogoTitulo>
            <DialogoDescricao>
              Anote ou copie agora: por segurança, ela não será exibida novamente.
            </DialogoDescricao>
          </DialogoCabecalho>

          <DialogoCorpo>
            <p className="text-sm">
              Entregue esta senha a <strong>{senhaGerada?.nome}</strong>. No primeiro acesso, o
              sistema exigirá a troca por uma senha pessoal.
            </p>

            <div className="bg-silenciado flex items-center justify-between gap-2 rounded-md border p-3">
              <code className="font-mono text-lg tracking-wider select-all">
                {senhaGerada?.senha}
              </code>
              <Botao
                variante="contorno"
                tamanho="sm"
                onClick={() => void copiarSenha()}
                aria-label="Copiar senha"
              >
                <Copy aria-hidden="true" />
                Copiar
              </Botao>
            </div>
          </DialogoCorpo>

          <DialogoRodape>
            <Botao onClick={() => setSenhaGerada(null)}>Já anotei</Botao>
          </DialogoRodape>
        </DialogoConteudo>
      </Dialogo>
    </>
  );
}
